// A place's picture as the time of day has it: the current look's pictures (and the foreground copied from them),
// crossfading into the next look when the phase changes (street/lookTransition.mjs). Entering shows the current look
// directly; a look that isn't loaded yet never starts a fade, so the place never goes empty under the sky. Used by
// the street (street/streetLook.js) and the yards (MainScene, BackyardScene and their visits).
//
//   layersFor(look)          -> [{ key, x, y, w, h, originX, originY }]   the pictures of that look, read live
//   foregroundFor(look, rects) -> { setAlpha(a), destroy() } | null      what is copied from them to stand in front
//   prefetch(look)           -> keys to ask for late in each phase (the next look's), on top of a wanted look's own
//   prioritise()             -> re-plans the background loader once a fade is over (a visit passes its host's state)
//   ownOutlines              -> each look has its own sky cut (the yards; the street's looks share one): see below
//   stacked                  -> the pictures of a look lie behind one another (the Podwórko's far layer and its near
//                               picture), not side by side (the street's tiles): each picture of every look stays
//                               below the next picture of every look, fading ones included
// A picture rect may carry `scrollFactor` (the far layer's parallax).
import { renderImageWhenReady } from './world.js';
import { preloader } from '../scenes/PreloadScene.js';
import { prioritiseFor } from '../ui/sceneLoading.js';
import { cycleT } from '../farm/skyClock.mjs';
import { clockInput } from '../farm/skyLayer.js';
import { lookReady, lookTransition, prefetchNextNow, startLook } from '../street/lookTransition.mjs';

const INCOMING_DEPTH = 0.01;   // just above the shown pictures (depth 0), below everything standing in the place
const KEEP_DEPTH = 0.005;      // the old look where the new one has ground (ownOutlines), between the two
const STACK_STEP = 0.006;      // stacked: picture i+1 of any look above picture i of every look

// When the looks' sky cuts differ (the yards), the old look drawn whole under the fading-in new one shows its own
// ground where the new one has sky — night's hills stood as a dark band over the dawn sky for the 8 s of a fade
// (owner 2026-11-02). With `ownOutlines` the old look fades out instead, and a copy of it, masked by the new look's
// alpha, stays solid only where the new one has ground too: there the new look fades in over it as before.

export function createPhasedLook(scene, { phase, looks, lookFor, layersFor, foregroundFor = () => null, prefetch = () => [],
  prioritise = () => prioritiseFor(scene.scene.key), ownOutlines = false, stacked = false }) {
  // depth of picture i in a role: the shown look 0, its masked copy KEEP_DEPTH, the incoming look INCOMING_DEPTH
  const depthOf = (role, i) => (stacked ? i * STACK_STEP + role / INCOMING_DEPTH * (STACK_STEP / 2) : role);
  const keysOf = (look) => layersFor(look).map((r) => r.key);
  const ready = (look) => lookReady(keysOf(look), (k) => scene.textures.exists(k));
  let state = { shown: startLook(lookFor(phase), ready, looks), incoming: null, f: 0 };
  const asked = new Set();

  const draw = (look, depth) => {
    const rects = layersFor(look);
    const imgs = rects.map((r, i) => renderImageWhenReady(scene, r.key, r.x, r.y, r.w, r.h, r.originX, r.originY)
      .setDepth(depthOf(depth, i)).setScrollFactor(r.scrollFactor ?? 1));
    const fg = foregroundFor(look, rects);
    return { look, imgs, fg };
  };
  const remove = (layer) => {
    if (!layer) return;
    for (const img of layer.imgs) img.destroy();
    layer.fg?.destroy();
    for (const k of layer.keep ?? []) { k.dup.clearMask(true); k.dup.destroy(); k.maskImg.destroy(); }
  };
  // The old look's copy that stays solid where the incoming look has ground (ownOutlines).
  const keepUnder = (from, to) => from.imgs.map((img, i) => {
    const t = to.imgs[i];
    const maskImg = scene.make.image({ x: t.x, y: t.y, key: t.texture.key, add: false })
      .setOrigin(t.originX, t.originY).setDisplaySize(t.displayWidth, t.displayHeight).setScrollFactor(t.scrollFactorX);
    const dup = scene.add.image(img.x, img.y, img.texture.key).setOrigin(img.originX, img.originY)
      .setDisplaySize(img.displayWidth, img.displayHeight).setDepth(depthOf(KEEP_DEPTH, i)).setScrollFactor(img.scrollFactorX);
    dup.setMask(maskImg.createBitmapMask());
    return { dup, maskImg };
  });
  const setAlpha = (layer, a) => {
    for (const img of layer.imgs) img.setAlpha(a);
    layer.fg?.setAlpha(a);
  };
  // A phone fetched no other look ahead (assetManifest otherPhases); asking for its keys puts them first in line.
  const ask = (keys) => {
    for (const k of keys) {
      if (asked.has(k) || scene.textures.exists(k)) continue;
      asked.add(k);
      preloader.whenReady(k, () => {});
    }
  };

  let shown = draw(state.shown, 0);
  let incoming = null;

  return {
    get shownLook() { return shown?.look; },
    update(phaseNow, dtMs) {
      const look = lookFor(phaseNow);
      if (look !== state.shown) ask(keysOf(look));
      if (prefetchNextNow(cycleT(clockInput()))) ask(prefetch(look));
      const next = lookTransition(state, { look, ready, dtMs });
      if (next === state) return;
      if (next.shown !== shown.look) {
        // The fade is over: the incoming look is the place now, the old one goes (on phones its textures are freed by
        // the loader's next pass, which prioritiseFor triggers).
        remove(shown);
        if (incoming?.look === next.shown) {
          shown = incoming;
          incoming = null;
          remove({ imgs: [], keep: shown.keep });   // the old look's masked copy goes with the old look
          shown.keep = null;
          shown.imgs.forEach((img, i) => img.setDepth(depthOf(0, i)));
        } else {
          remove(incoming);
          incoming = null;
          shown = draw(next.shown, 0);
        }
        setAlpha(shown, 1);
        asked.clear();
        prioritise();
      }
      if (next.incoming) {
        if (!incoming) {
          incoming = draw(next.incoming, INCOMING_DEPTH);
          if (ownOutlines) incoming.keep = keepUnder(shown, incoming);
        }
        setAlpha(incoming, next.f);
        if (ownOutlines) for (const img of shown.imgs) img.setAlpha(1 - next.f);
      }
      state = next;
    },
    // The pictures behind the look changed (a level upgrade): draw the shown look again at once, no fade.
    redraw() {
      remove(incoming);
      incoming = null;
      remove(shown);
      state = { shown: state.shown, incoming: null, f: 0 };
      shown = draw(state.shown, 0);
    },
    destroy() {
      remove(incoming);
      remove(shown);
      incoming = null;
      shown = null;
    },
  };
}
